import type { LobbyPublic, PlayerPublic } from '../../shared/types.js';
import { plural, serverText, t } from '../i18n/i18n.js';
import { clear, el, hex } from './dom.js';

/**
 * The grid: what you see while the camera circles the city and everyone says they are ready.
 *
 * Laid out around the edges rather than over the middle, because the whole point of the
 * phase is the shot playing behind it. Anything that would cover the city has to earn it,
 * and only one thing does — the ready button, which is the single action available and
 * should be impossible to miss.
 */

export interface StagingOptions {
  onReady(ready: boolean): void;
  /** Host only: go now, whoever is still deciding. */
  onForceStart(): void;
}

export class StagingScreen {
  readonly root: HTMLElement;
  private readonly title: HTMLElement;
  private readonly subtitle: HTMLElement;
  private readonly weatherNode: HTMLElement;
  private readonly driverList: HTMLElement;
  private readonly standingsPanel: HTMLElement;
  private readonly standingsBody: HTMLElement;
  private readonly readyButton: HTMLButtonElement;
  private readonly readyMain: HTMLElement;
  private readonly readySub: HTMLElement;
  private readonly forceButton: HTMLButtonElement;
  private readonly waitingNote: HTMLElement;

  private selfReady = false;

  constructor(private readonly options: StagingOptions) {
    this.title = el('h2', { class: 'staging-title' }, t('Get ready'));
    this.subtitle = el('p', { class: 'staging-subtitle' }, '');
    this.weatherNode = el('div', { class: 'staging-weather' });
    this.driverList = el('ul', { class: 'staging-drivers', id: 'staging-drivers' });
    this.standingsBody = el('tbody', {});
    this.standingsPanel = el(
      'div',
      { class: 'staging-standings is-hidden' },
      el('h3', { class: 'panel-title' }, t('Standings')),
      el('table', { class: 'staging-standings-table' }, this.standingsBody),
    );

    this.readyMain = el('span', { class: 'staging-ready-main' }, t("I'm ready"));
    this.readySub = el('span', { class: 'staging-ready-sub' }, t('the lights wait for you'));
    this.readyButton = el(
      'button',
      { class: 'button staging-ready', type: 'button', id: 'staging-ready' },
      this.readyMain,
      this.readySub,
    );
    this.readyButton.addEventListener('click', () => {
      this.selfReady = !this.selfReady;
      this.paintReadyButton();
      this.options.onReady(this.selfReady);
    });

    this.forceButton = el(
      'button',
      { class: 'button button-ghost staging-force is-hidden', type: 'button', id: 'staging-force' },
      t('Start now'),
    );
    this.forceButton.addEventListener('click', () => this.options.onForceStart());

    this.waitingNote = el('p', { class: 'staging-waiting' }, '');

    this.root = el(
      'div',
      { class: 'staging is-hidden', id: 'staging' },
      el('div', { class: 'staging-header' }, this.title, this.subtitle, this.weatherNode),
      el(
        'div',
        { class: 'staging-side' },
        el('h3', { class: 'panel-title' }, t('On the grid')),
        this.driverList,
        this.standingsPanel,
      ),
      el(
        'div',
        { class: 'staging-footer' },
        this.readyButton,
        this.waitingNote,
        this.forceButton,
      ),
    );
  }

  update(lobby: LobbyPublic, localId: string, regionLabel: string): void {
    const self = lobby.players.find((p) => p.id === localId);
    const isHost = Boolean(self?.host);
    // Taken from the server rather than remembered locally: a reconnect mid-staging must
    // show the state the server actually holds, not the last button this tab pressed.
    this.selfReady = Boolean(self?.ready);
    this.paintReadyButton();

    const tour = lobby.tour;
    if (tour) {
      const stop = tour.stops[tour.index];
      this.title.textContent = t('Race {n} of {total}', { n: tour.index + 1, total: tour.stops.length });
      this.subtitle.textContent = stop ? `${stop.title} · ${tour.name}` : tour.name;
    } else {
      this.title.textContent = t('Get ready');
      this.subtitle.textContent = regionLabel;
    }

    this.weatherNode.textContent = serverText(lobby.weather?.label ?? '');
    this.weatherNode.classList.toggle('is-hidden', !lobby.weather);

    clear(this.driverList);
    for (const player of lobby.players) {
      // A driver whose browser is still pulling the city is not "ready" however green their
      // lobby button was — the grid is waiting for them either way, and saying otherwise is
      // how you end up wondering why the lights have not gone out.
      const goodToGo = player.ai || (player.ready && player.worldStatus === 'ready');
      this.driverList.append(
        el(
          'li',
          {
            class: `staging-driver${player.id === localId ? ' is-self' : ''}${
              goodToGo ? ' is-ready' : ''
            }`,
          },
          el('span', { class: 'swatch', style: { background: hex(player.color) } }),
          el('span', { class: 'player-name' }, player.name),
          player.ai ? el('span', { class: 'tag' }, 'AI') : driverState(player),
        ),
      );
    }

    this.paintStandings(lobby);

    const humans = lobby.players.filter((p) => !p.ai && p.connected);
    const loading = humans.filter((p) => p.worldStatus === 'loading');
    const waiting = humans.filter((p) => p.worldStatus !== 'loading' && !p.ready);
    // Once you have said yes, the only thing left to know is who everybody is waiting for —
    // so that line, not the button, becomes the loud one.
    //
    // Two sentences for two waits, because only one of them has a button that ends it: the
    // host can start without somebody who has not pressed ready, and cannot start without
    // somebody whose browser has not finished downloading the city.
    if (loading.length > 0) {
      this.waitingNote.textContent = plural(
        loading.length,
        'Downloading the world · {names} is not on the grid yet',
        'Downloading the world · {names} are not on the grid yet',
        { names: listNames(loading.map((p) => p.name)) },
      );
    } else if (waiting.length > 0) {
      this.waitingNote.textContent = t('{ready}/{total} ready · waiting for {names}', {
        ready: humans.length - waiting.length,
        total: humans.length,
        names: listNames(waiting.map((p) => p.name)),
      });
    } else {
      this.waitingNote.textContent = t('Everyone is ready — lights out shortly.');
    }
    this.waitingNote.classList.toggle(
      'is-loud',
      this.selfReady && waiting.length + loading.length > 0,
    );
    // Hidden rather than disabled while a world is in flight: "Start now" would be a button
    // that does nothing, and the line above already says what is being waited for.
    this.forceButton.classList.toggle(
      'is-hidden',
      !isHost || loading.length > 0 || waiting.length === 0,
    );
  }

  /**
   * The standings so far, between races.
   *
   * Deliberately not shown before the first race of a cup, where every row reads zero and
   * the only thing the table communicates is that nothing has happened yet.
   */
  private paintStandings(lobby: LobbyPublic): void {
    const tour = lobby.tour;
    const show = Boolean(tour && tour.index > 0);
    this.standingsPanel.classList.toggle('is-hidden', !show);
    if (!tour || !show) return;

    clear(this.standingsBody);
    for (const row of tour.standings) {
      this.standingsBody.append(
        el(
          'tr',
          {},
          el('td', { class: 'tabular' }, String(row.rank)),
          el(
            'td',
            {},
            el('span', { class: 'swatch', style: { background: hex(row.color) } }),
            el('span', {}, row.name),
          ),
          el('td', { class: 'tabular' }, String(row.points)),
        ),
      );
    }
  }

  /**
   * The button was read backwards by everyone who met it.
   *
   * It used to go solid green the moment you pressed it, which made the *confirmed* state
   * the loudest thing on the screen — so it looked like the thing still asking to be
   * pressed, people pressed it again to be sure, and un-readied themselves. Then they
   * waited, watching a race that would not start, in front of a big green button.
   *
   * So the emphasis is inverted. Unpressed is the bright, pulsing, filled call to action;
   * pressed is a quiet outline that says what it is rather than what to do, and spells out
   * that pressing again undoes it.
   */
  private paintReadyButton(): void {
    this.readyMain.textContent = this.selfReady ? t("You're ready ✓") : t("I'm ready");
    this.readySub.textContent = this.selfReady ? t('tap to cancel') : t('the lights wait for you');
    this.readyButton.classList.toggle('is-active', this.selfReady);
  }

  show(): void {
    this.root.classList.remove('is-hidden');
  }

  hide(): void {
    this.root.classList.add('is-hidden');
  }
}

/** The right-hand label on a driver's row: where their browser is, then where they are. */
function driverState(player: PlayerPublic): HTMLElement {
  if (player.worldStatus === 'loading') {
    return el('span', { class: 'player-ready is-loading' }, t('downloading'));
  }
  if (player.worldStatus === 'failed') {
    return el('span', { class: 'player-ready is-failed' }, t('no world'));
  }
  return el(
    'span',
    { class: `player-ready${player.ready ? ' is-ready' : ''}` },
    player.ready ? t('ready') : t('waiting'),
  );
}

function listNames(names: string[]): string {
  if (names.length === 1) return names[0];
  if (names.length === 2) return t('{first} and {last}', { first: names[0], last: names[1] });
  return t('{first} and {last}', { first: names.slice(0, -1).join(', '), last: names[names.length - 1] });
}
